<div class="hd-life-timeline">
  <div class="hd-life-timeline__toolbar">
    <span class="hd-life-timeline__zoom-label">{{ 'humanDesign.lifeTimeline.zoomLabel' | transloco }}</span>
    @for (level of zoomLevels; track level) {
      <button
        type="button"
        class="hd-life-timeline__zoom"
        [class.is-active]="zoom() === level"
        [attr.aria-pressed]="zoom() === level"
        (click)="setZoom(level)"
        [attr.data-test-id]="'life-timeline-zoom-' + level"
      >
        {{ zoomLabelKey(level) | transloco }}
      </button>
    }

    <span class="hd-life-timeline__toolbar-spacer"></span>

    <button
      type="button"
      class="hd-life-timeline__zoom hd-life-timeline__labels-toggle"
      [class.is-active]="showLabels()"
      [attr.aria-pressed]="showLabels()"
      (click)="showLabels.set(!showLabels())"
      data-test-id="life-timeline-labels-toggle"
    >
      {{ 'humanDesign.lifeTimeline.labelsToggle' | transloco }}
    </button>

    <button
      type="button"
      class="hd-life-timeline__zoom"
      [class.is-active]="colorMode() === 'mood'"
      [attr.aria-pressed]="colorMode() === 'mood'"
      (click)="colorMode.set(colorMode() === 'mood' ? 'moment' : 'mood')"
      data-test-id="life-timeline-color-mode-toggle"
    >
      {{ 'humanDesign.lifeTimeline.moodColorToggle' | transloco }}
    </button>

    <hd-timeline-help [entries]="helpEntries"></hd-timeline-help>
  </div>

  <div
    #scroller
    class="hd-life-timeline__scroller"
    [class.is-panning]="panning()"
    tabindex="0"
    (pointerdown)="onPanPointerDown($event)"
    (pointerup)="onPanPointerUp($event)"
    (pointercancel)="onPanPointerUp($event)"
    (keydown)="onScrollKeydown($event)"
  >
    <div
      class="hd-life-timeline__track"
      [style.width.px]="trackWidthPx()"
      [style.height.px]="trackHeightPx()"
      (click)="onTrackClick($event)"
      data-test-id="life-timeline-track"
    >
      <!-- Buiten het hoofdbereik (vóór de geboorte, na ±84 jaar): gedempt, bereikbaar via scrollen. -->
      @if (outsideFocus(); as outside) {
        <div class="hd-life-timeline__outside" [style.left.px]="0" [style.width.px]="outside.beforeWidthPx"
             data-test-id="life-timeline-outside-before"></div>
        <div class="hd-life-timeline__outside" [style.left.px]="outside.afterLeftPx" [style.width.px]="outside.afterWidthPx"
             data-test-id="life-timeline-outside-after"></div>
      }

      <!-- As per zoomniveau: jaren, maanden of maanden + weekdagen; leeftijd op de jaargrenzen. -->
      @for (tick of ticks(); track $index) {
        <div class="hd-life-timeline__tick"
             [class.hd-life-timeline__tick--minor]="tick.minor"
             [style.left.px]="tick.xPx">
          @if (tick.label) {
            <span class="hd-life-timeline__tick-year"
                  [class.hd-life-timeline__tick-year--minor]="tick.minor">{{ tick.label }}</span>
          }
          @if (tick.age != null) {
            <span class="hd-life-timeline__tick-age">{{ 'humanDesign.lifeTimeline.ageShort' | transloco: { age: tick.age } }}</span>
          }
        </div>
      }

      <div class="hd-life-timeline__today" [style.left.px]="todayXPx()"
           [title]="'humanDesign.lifeTimeline.today' | transloco"></div>

      <!-- Systeemrij: berekende cycli, direct onder de as (beslispunt 6). -->
      @for (cycle of cycleDots(); track cycle.marker.id) {
        <button
          type="button"
          class="hd-life-timeline__dot hd-life-timeline__dot--cycle"
          [style.left.px]="cycle.xPx"
          [style.top.px]="cycleTopPx()"
          [title]="cycle.marker.label"
          (click)="selectCycle(cycle, $event)"
          data-test-id="life-timeline-cycle"
        >
          <i class="fas fa-{{ cycle.marker.icon }}" aria-hidden="true"></i>
        </button>
      }

      <!-- Rijen: elk moment/range een eigen plek, overlap krijgt een nieuwe rij. -->
      @for (item of layout().items; track item.marker.id) {
        @if (item.label != null) {
          <span
            class="hd-life-timeline__item-label"
            [style.left.px]="labelLeftPx(item)"
            [style.top.px]="labelTopPx(item)"
          >{{ item.label }}</span>
        }
        @if (item.widthPx == null) {
          <button
            type="button"
            class="hd-life-timeline__dot"
            [class.hd-life-timeline__dot--colored]="itemColor(item) != null"
            [style.left.px]="item.xPx"
            [style.top.px]="dotTopPx(item)"
            [style.background]="itemColor(item)"
            [title]="item.marker.label"
            (click)="selectItem(item, $event)"
            data-test-id="life-timeline-dot"
          >
            <i class="fas fa-{{ item.marker.icon }}" aria-hidden="true"></i>
          </button>
        } @else {
          <button
            type="button"
            class="hd-life-timeline__range"
            [style.left.px]="item.xPx"
            [style.width.px]="item.widthPx"
            [style.top.px]="barTopPx(item)"
            [style.background]="itemColor(item)"
            [title]="item.marker.label"
            (click)="selectItem(item, $event)"
            data-test-id="life-timeline-range"
          ></button>
        }
      }

      <!-- Transit-strip: poort-blokken per (trage) planeet, alleen op fijne zooms (spec §2.5). -->
      @if (stripVisible()) {
        @for (lane of transitLanes(); track lane.planetKey; let laneIndex = $index) {
          <div class="hd-life-timeline__strip-lane"
               [style.top.px]="stripTopPx() + laneIndex * 18"
               data-test-id="life-timeline-strip-lane">
            @for (block of lane.blocks; track block.fromMs) {
              <span
                class="hd-life-timeline__strip-block"
                [style.left.px]="stripBlockLeftPx(block)"
                [style.width.px]="stripBlockWidthPx(block)"
                [style.background]="lane.color"
                [title]="lane.label + ' · ' + stripBlockTitle(block)"
              ></span>
            }
          </div>
        }
      }

      <!-- Popover van de geselecteerde marker. -->
      @if (selection(); as selected) {
        <div class="hd-life-timeline__popover" [style.left.px]="selected.xPx" [style.top.px]="selected.topPx"
             (click)="$event.stopPropagation()" data-test-id="life-timeline-popover">
          <div class="hd-life-timeline__popover-item">
            <i class="fas fa-{{ selected.marker.icon }} hd-life-timeline__popover-icon" aria-hidden="true"></i>
            <div class="hd-life-timeline__popover-text">
              <span class="hd-life-timeline__popover-label">{{ selected.marker.label }}</span>
              <span class="hd-life-timeline__popover-moment">{{ markerMomentText(selected.marker) }}</span>
              @if (selected.marker.category || (selected.marker.emotions?.length ?? 0) > 0) {
                <span class="hd-life-timeline__popover-tags" data-test-id="life-timeline-popover-tags">
                  @if (selected.marker.category) {
                    <span>{{ 'humanDesign.momentFields.category.' + selected.marker.category | transloco }}</span>
                  }
                  @for (emotion of selected.marker.emotions ?? []; track emotion) {
                    <span>{{ emotionLabelKey(emotion) | transloco }}</span>
                  }
                </span>
              }
            </div>
            @if (selected.marker.mood != null) {
              <span class="hd-life-timeline__popover-mood" [style.background]="moodColor(selected.marker.mood)"
                    [title]="'humanDesign.momentFields.mood.' + selected.marker.mood | transloco"
                    data-test-id="life-timeline-popover-mood"></span>
            }
            @if (selected.marker.vitality != null) {
              <i class="fas fa-{{ vitalityIcons[selected.marker.vitality] }} hd-life-timeline__popover-indicator"
                 [title]="'humanDesign.momentFields.vitality.' + selected.marker.vitality | transloco"
                 aria-hidden="true"></i>
            }
            @if (selected.marker.hasNote) {
              <i class="fas fa-note-sticky hd-life-timeline__popover-indicator" aria-hidden="true"></i>
            }
            @if ((selected.marker.attachmentCount ?? 0) > 0) {
              <span class="hd-life-timeline__popover-indicator">
                <i class="fas fa-paperclip" aria-hidden="true"></i>{{ selected.marker.attachmentCount }}
              </span>
            }
            @if (selected.marker.openChartId) {
              <button type="button" class="hd-life-timeline__popover-open"
                      [title]="'humanDesign.lifeTimeline.edit' | transloco"
                      (click)="editMarker(selected.marker)" data-test-id="life-timeline-edit">
                <i class="fas fa-pen" aria-hidden="true"></i>
              </button>
            }
            <!-- Openen: een chart, of (I Tjing-moment) de raadpleging in de dialoog. -->
            @if (selected.marker.openChartId || selected.marker.consultationId) {
              <button type="button" class="hd-life-timeline__popover-open"
                      (click)="openMarker(selected.marker)" data-test-id="life-timeline-open">
                {{ 'humanDesign.lifeTimeline.open' | transloco }}
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>

  <div class="hd-life-timeline__hint">{{ 'humanDesign.lifeTimeline.addHint' | transloco }}</div>
</div>
